import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { detectUILangSync, detectUILangAsync } from "../../utils/detectUILang";
import { Button, Input, Tabs } from "antd";
import { Navigate, useNavigate } from "react-router-dom";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import CopyToClipboardCode from "../../components/copyToClipboardCode/CopyToClipboardCode";
import { createV2ApiKey, listV2ApiKeys, revokeV2ApiKey } from "../../api/v2/apiKeys";
import { postAgentChat } from "../../api/v2/agent";
import "../onboarding/v2-onboarding-agent.css";
import "./v2-copilot.css";
import VuelveLogo from "../../assets/images/logo-bright.png";
import FlagUS from "../../assets/images/flags/us.png";
import FlagIL from "../../assets/images/flags/il.png";
import {getPricingPlans} from "../../api/getPricingPlans";
import {PricingPlanProps} from "../../types/PricingPlanProps";
import {ToastSkin} from "../../context/ToastContext";
import useToast from "../../hooks/useToast";
import {getNextCyclePricingPlan} from "../../api/getNextCyclePricingPlan";
import {getV2PricingPlans} from "../../api/getV2PricingPlans";
import {createPhoneNumber, CreatePhoneRequest} from "../../api/createPhoneNumber";
import {getWhatsappProfile} from "../../api/getWhatsappProfile";
import {
    GetV2OnboardingPhoneStatus,
    RegisterV2OnboardingPhoneNumber,
    SendV2ByonPhoneCode,
    VerifyV2ByonPhoneCode
} from "../../api/v2/onboarding";
import {
    PhoneNumberObj,
} from "../../components/whatsappBusinessAccount/onBoardingProcess/twilioPhoneNumberSelector/TwilioPhoneCatalogPicker";
import Step2PhoneFlowBubble from "../../components/copilot/Step2PhoneFlowBubble";
import Step3MetaFlowBubble from "../../components/copilot/Step3MetaFlowBubble";
import Step1PricingFlowBubble from "../../components/copilot/Step1PricingFlowBubble";
import StepIntroBubble from "../../components/copilot/StepIntroBubble";
import useStep2Flow from "../../hooks/copilot/useStep2Flow";
import { isStep2MessageType } from "../../types/copilot/step2";
import usePaymentFlow from "../../hooks/usePaymentFlow";
import useFlag from "../../hooks/useFlag";
import {hasEffectivePricingPlan} from "../../utils/pricingPlanStatus";

const VIVO_LANG_KEY = "v2_vivo_lang";
type UILang = "en" | "he";
const LANG_OPTIONS: { code: UILang; label: string; flagSrc: string }[] = [
    { code: "en", label: "English", flagSrc: FlagUS },
    { code: "he", label: "עברית", flagSrc: FlagIL },
];

const VIVO_TR = {
    en: {
        welcome_title: "Welcome to Vuelve",
        welcome_intro: "Thanks for trusting us. To get your account fully live and start connecting with clients, we just need to complete a few quick steps:",
        step_pricing: "Select your preferred pricing plan",
        step_phone: "Choose a business phone number",
        step_waba: "Create and connect a WhatsApp Business Account",
        step_keys: "Get the keys and snippets to add Vuelve to your site",
        welcome_after: "Once these are done, you can move on to building automations and messaging flows.",
        phone_selected_title: "Phone number selected",
        phone_selected_body_byon: (phone: string) => `You've chosen ${phone} as your business number. The next step is selecting a pricing plan to activate your account.`,
        phone_selected_body_twilio: (phone: string) => `You've chosen ${phone} as your business number. The next step is selecting a pricing plan to activate your account.`,
        phone_selected_next_byon: "Since you're bringing your own number, no purchase is needed — just select a plan and we'll verify and register your number.",
        phone_selected_next_twilio: "Once your plan is selected, we'll purchase the selected number, or an equivalent from the same country if it's no longer available.",
        almost_title: "You're almost there",
        almost_plan: (name: string) => `You previously selected the ${name} plan.`,
        phone_included_iso: (iso: string) => ` Your plan includes a phone number for ${iso} region(s).`,
        phone_included_generic: " Your plan includes a phone number.",
        change_plan_prefix: "To change your selected plan, visit",
        settings_payments: "Settings → Payments",
        almost_next: "To activate your account, the next step is choosing a business phone number and connecting a WhatsApp Business Account to it. You can bring your own number or pick one from us.",
        connect_waba_title: "Let's connect WhatsApp",
        your_plan_is: "Your selected plan is",
        to_change_visit: "To change it, visit",
        your_phone_is: "Your selected phone number is",
        connect_waba_next: "Next, connect this number to your WhatsApp Business Account in Meta so messaging can go live.",
        whatsapp_intro_title: "Step 1: Choose your WhatsApp number",
        whatsapp_intro_p1: "This number will appear on your WhatsApp Business profile and send messages to your customers.",
        whatsapp_intro_p2: "Do you already have a phone number you'd like to use for your new WhatsApp account?",
        whatsapp_intro_p3: "It must not be connected to any existing WhatsApp account and must be able to receive SMS for verification.",
        whatsapp_intro_p4: "Your plan also includes a free number from Vuelve, so if you don't have one, we've got you covered.",
        done_step_title: "WhatsApp connected successfully",
        done_phone_connected: "Your selected phone number is now connected to your WhatsApp account.",
        done_selected_plan: "Selected plan:",
        done_selected_phone: "Selected phone number:",
        done_waba_label: "WhatsApp Business Account:",
        done_connected: "Connected ✓",
        done_not_connected: "Not connected",
        done_not_available: "Not available yet",
        done_shopwoo_title: "Onboarding is completed.",
        done_shopwoo_body: "Vuelve can now receive abandoned carts and other events from your store platform, and send custom automation flows to your customers.",
        done_start_automations: "Start creating automations",
        done_full_title: "You are fully onboarded.",
        done_full_body: "Your plan, phone number, and WhatsApp Business Account are ready.",
        done_tracking: "We hope your tracking tags are already implemented in your website. If not, it is a good idea to do that now. If you need help, ask Vivo here in the copilot.",
        done_tracking_next: "Once tracking is ready, you can start creating automations triggered by user activity and events.",
        done_show_snippets: "Show snippets",
        done_continue: "You can continue by managing and expanding your automations.",
        meta_opened: "A Meta window just opened. Complete the WhatsApp setup there, then come back here when you're done.",
        meta_complete: "Great, your Meta onboarding is complete.",
        meta_failed: "Meta onboarding was not completed. Please try again and finish the flow in the Meta window.",
        chat_error: "Sorry, something went wrong.",
        cta_select_plan: "Select a pricing plan",
        cta_select_phone: "Select a phone number",
        cta_reconnect_whatsapp: "Try connecting WhatsApp again",
        cta_get_keys: "Get keys & snippets",
        placeholder_cta: (cta: string) => `Ask a question or type "${cta}"`,
        placeholder_default: "Ask any question here",
    },
    he: {
        welcome_title: "ברוכים הבאים ל-Vuelve",
        welcome_intro: "תודה שבחרתם בנו. כדי להפעיל את החשבון ולהתחיל לתקשר עם לקוחות, נצטרך להשלים כמה שלבים קצרים:",
        step_pricing: "בחרו את תוכנית המחיר המועדפת",
        step_phone: "בחרו מספר טלפון עסקי",
        step_waba: "צרו וחברו חשבון WhatsApp Business",
        step_keys: "קבלו את המפתחות והקטעי קוד להוספת Vuelve לאתר",
        welcome_after: "לאחר שהכל יהיה מוכן, תוכלו לעבור לבניית אוטומציות וזרימות הודעות.",
        phone_selected_title: "מספר הטלפון נבחר",
        phone_selected_body_byon: (phone: string) => `בחרתם את ${phone} כמספר הטלפון העסקי שלכם. השלב הבא הוא בחירת תוכנית מחיר להפעלת החשבון.`,
        phone_selected_body_twilio: (phone: string) => `בחרתם את ${phone} כמספר הטלפון העסקי שלכם. השלב הבא הוא בחירת תוכנית מחיר להפעלת החשבון.`,
        phone_selected_next_byon: "מכיוון שאתם מביאים מספר משלכם, אין צורך ברכישה — פשוט בחרו תוכנית ואנחנו נאמת ונרשום את המספר עבורכם.",
        phone_selected_next_twilio: "לאחר בחירת התוכנית, נרכוש את המספר הנבחר, או מספר שקול מאותה מדינה אם המספר המבוקש אינו זמין עוד.",
        almost_title: "כמעט שם",
        almost_plan: (name: string) => `בחרתם קודם בתוכנית ${name}.`,
        phone_included_iso: (iso: string) => ` התוכנית שלכם כוללת מספר טלפון לאזור ${iso}.`,
        phone_included_generic: " התוכנית שלכם כוללת מספר טלפון.",
        change_plan_prefix: "לשינוי התוכנית הנבחרת, עברו אל",
        settings_payments: "הגדרות ← תשלומים",
        almost_next: "להפעלת החשבון, השלב הבא הוא בחירת מספר טלפון עסקי וחיבורו לחשבון WhatsApp Business. תוכלו להביא מספר משלכם או לבחור אחד מאיתנו.",
        connect_waba_title: "בואו נחבר WhatsApp",
        your_plan_is: "התוכנית שבחרתם היא",
        to_change_visit: "לשינוי, עברו אל",
        your_phone_is: "מספר הטלפון שבחרתם הוא",
        connect_waba_next: "כעת, חברו מספר זה לחשבון WhatsApp Business שלכם ב-Meta כדי שההודעות יוכלו לצאת לדרך.",
        whatsapp_intro_title: "שלב 1: בחרו את מספר ה-WhatsApp שלכם",
        whatsapp_intro_p1: "מספר זה יופיע בפרופיל ה-WhatsApp Business שלכם וישלח הודעות ללקוחות.",
        whatsapp_intro_p2: "האם יש לכם כבר מספר טלפון שתרצו להשתמש בו לחשבון ה-WhatsApp החדש?",
        whatsapp_intro_p3: "המספר לא יכול להיות מחובר לאף חשבון WhatsApp קיים וחייב להיות מסוגל לקבל SMS לצורך אימות.",
        whatsapp_intro_p4: "התוכנית שלכם כוללת גם מספר חינמי מ-Vuelve, כך שאם אין לכם מספר, אנחנו כאן בשבילכם.",
        done_step_title: "WhatsApp חובר בהצלחה",
        done_phone_connected: "מספר הטלפון שבחרתם מחובר כעת לחשבון ה-WhatsApp שלכם.",
        done_selected_plan: "תוכנית נבחרת:",
        done_selected_phone: "מספר טלפון נבחר:",
        done_waba_label: "חשבון WhatsApp Business:",
        done_connected: "מחובר ✓",
        done_not_connected: "לא מחובר",
        done_not_available: "לא זמין עדיין",
        done_shopwoo_title: "תהליך ההצטרפות הושלם.",
        done_shopwoo_body: "Vuelve יכולה כעת לקבל עגלות נטושות ואירועים אחרים מפלטפורמת החנות שלכם ולשלוח זרימות אוטומציה מותאמות ללקוחות.",
        done_start_automations: "התחילו ליצור אוטומציות",
        done_full_title: "ההצטרפות הושלמה במלואה.",
        done_full_body: "התוכנית, מספר הטלפון וחשבון ה-WhatsApp Business שלכם מוכנים.",
        done_tracking: "אנו מקווים שתגיות המעקב כבר מוטמעות באתר שלכם. אם לא, כדאי לעשות זאת עכשיו. אם תזדקקו לעזרה, שאלו את Vivo כאן.",
        done_tracking_next: "לאחר שהמעקב מוכן, תוכלו להתחיל ליצור אוטומציות המופעלות על ידי פעילות ואירועי משתמש.",
        done_show_snippets: "הצג קטעי קוד",
        done_continue: "תוכלו להמשיך לנהל ולהרחיב את האוטומציות שלכם.",
        meta_opened: "חלון Meta נפתח. השלימו את הגדרת ה-WhatsApp שם, ואז חזרו לכאן כשתסיימו.",
        meta_complete: "מצוין, תהליך ה-onboarding ב-Meta הושלם בהצלחה.",
        meta_failed: "תהליך ה-onboarding ב-Meta לא הושלם. אנא נסו שוב וסיימו את הזרימה בחלון Meta.",
        chat_error: "מצטערים, משהו השתבש.",
        cta_select_plan: "בחרו תוכנית מחיר",
        cta_select_phone: "בחרו מספר טלפון",
        cta_reconnect_whatsapp: "נסו שוב לחבר WhatsApp",
        cta_get_keys: "קבלו מפתחות וקטעי קוד",
        placeholder_cta: (cta: string) => `שאלו שאלה או כתבו "${cta}"`,
        placeholder_default: "שאלו כל שאלה כאן",
    },
};

type StepKey = "pricing" | "whatsapp" | "keys" | "done";

type ChatEntry = {
    id: string;
    role: "assistant" | "user";
    type?:
        | "text"
        | "meta_progress"
        | "welcome"
        | "pricing"
        | "whatsapp_summary"
        | "whatsapp"
        | "whatsapp_choice"
        | "whatsapp_own_form"
        | "whatsapp_own_sms_code"
        | "whatsapp_own_sms_failed"
        | "whatsapp_own_whatsapp_check"
        | "whatsapp_own_whatsapp_failed"
        | "whatsapp_own_success"
        | "whatsapp_vuelve_pick"
        | "whatsapp_meta"
        | "keys"
        | "done";
    text?: string;
    isRtl?: boolean;
};

const SDK_SCRIPT = (pubKey: string) =>
    `<script src="https://cdn.your-domain.com/vuelve/sdk.js?key=${pubKey}&baseUrl=http://vuelve-server.fly.dev" async></script>`;

const TYPES_DTS = `// src/types/vuelve.d.ts
export {};

declare global {
  interface Window {
    vuelve?: {
      identify: (payload: {
        client_id?: string;
        email?: string;
        phone?: string;
        first_name?: string;
        last_name?: string;
        full_name?: string;
      }) => void;
      track: (event_name: string, meta?: any) => void;
    };
  }
}
`;

const SendFilledIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.4325,4.86103 L15.4325,20.361 C15.175,21.0261 14.324,21.2156 13.8087,20.7227 L10.4266,17.4876 L8.35348,19.5607 C8.0385,19.8757 7.49993,19.6526 7.49993,19.2072 L7.49993,14.6882 L2.30868,9.72268 C1.74196,9.1806 1.99133,8.22685 2.75086,8.03155 L20.2509,3.53155 C21.0389,3.32889 21.7262,4.10218 21.4325,4.86103 Z M19,6.00006 L8.03159,13.1534 L9.76704,14.8134 L19,6.00006 Z" />
    </svg>
);

const V2CopilotPage = () => {
    const navigate = useNavigate();
    const { data: { shop, waba, flows }, actions: { loadShop, loadWaba } } = useAppContext();
    const { selectedDomain, isInitialLoadDone } = useUserContext();
    const [stepStack, setStepStack] = useState<StepKey[]>([]);
    const [apiKeys, setApiKeys] = useState<any[]>([]);
    const [revealedKeys, setRevealedKeys] = useState<{ public?: string; secret?: string }>({});
    const [keysLoading, setKeysLoading] = useState(false);
    const [pricingPlans, setPricingPlans] = useState<PricingPlanProps[] | null>(null);
    const [pricingPlansLoading, setPricingPlansLoading] = useState(false);
    const [pricingPlansIso, setPricingPlansIso] = useState('US');
    const [chatMessages, setChatMessages] = useState<ChatEntry[]>([
        { id: "welcome-1", role: "assistant", type: "welcome" },
    ]);
    const [chatInput, setChatInput] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [showPricingPlaceholder, setShowPricingPlaceholder] = useState(false);

    const [nextCyclePricingPlanId, setNextCyclePricingPlanId] = useState(null);
    const [phoneStepCompleted, setPhoneStepCompleted] = useState(false);
    const [, setPhoneChoice] = useState<"purchase" | "bring" | null>(null);
    const [showKeysAfterOnboarding, setShowKeysAfterOnboarding] = useState(false);
    const [selectedCatalogPhoneNumber, setSelectedCatalogPhoneNumber] = useState<PhoneNumberObj | null>(null);
    const [selectedByonPhoneNumber, setSelectedByonPhoneNumber] = useState<PhoneNumberObj | null>(null);
    const [isSavingPhoneSelection, setIsSavingPhoneSelection] = useState(false);
    const [isSendingByonCode, setIsSendingByonCode] = useState(false);
    const [isVerifyingByonCode, setIsVerifyingByonCode] = useState(false);
    const [byonCode, setByonCode] = useState("");
    const [byonVerifyError, setByonVerifyError] = useState<string | null>(null);
    const [byonVerifyAttempts, setByonVerifyAttempts] = useState(0);
    const [hasRegisteredOnboardingPhone, setHasRegisteredOnboardingPhone] = useState(false);
    const [pendingCatalogPhone, setPendingCatalogPhone] = useState<{ phoneNumber: string; isoCountry: string } | null>(null);
    const [isMetaInProgress, setIsMetaInProgress] = useState(false);
    const [isWhatsappProfileStatusLoaded, setIsWhatsappProfileStatusLoaded] = useState(false);
    const [isWhatsappProfileConnected, setIsWhatsappProfileConnected] = useState(false);
    const [uiLang, setUiLang] = useState<UILang>(() => {
        const stored = localStorage.getItem(VIVO_LANG_KEY);
        if (stored === "he" || stored === "en") return stored;
        return detectUILangSync();
    });
    useEffect(() => {
        const stored = localStorage.getItem(VIVO_LANG_KEY);
        if (stored === "he" || stored === "en") return;
        detectUILangAsync().then((lang) => setUiLang(lang));
    }, []);
    const currentLangOption = LANG_OPTIONS.find((o) => o.code === uiLang) ?? LANG_OPTIONS[0];
    const isRtlUI = uiLang === "he";
    const tr = VIVO_TR[uiLang];

    const messagesRef = useRef<HTMLDivElement | null>(null);
    const autoScrollRef = useRef(true);
    const isAutoRegeneratingKeysRef = useRef(false);
    const autoRegeneratedForCurrentKeysStepRef = useRef(false);
    const shopifyPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const shopifyPollAttemptsRef = useRef(0);

    const currentPricingPlan = shop?.data?.pricingPlan;
    const shopId = Number(shop?.data?.id || 0);
    const platformId = Number(shop?.data?.platformId || 0);
    const pricingPlanId = currentPricingPlan?.id;
    const selectedPricingPlan = useMemo(
        () => pricingPlans?.find((pp) => pp.id === pricingPlanId),
        [pricingPlans, pricingPlanId]
    );
    const hasPlan = hasEffectivePricingPlan(pricingPlanId, shopId);
    const isShopLoaded = Boolean((shop as any)?.isLoaded);

    const wabaAccount = waba?.data?.waba;
    const hasWabaRecord = Boolean(wabaAccount?.providerSenderId && wabaAccount?.active && !wabaAccount?.deleted);
    const hasWaba = hasWabaRecord && isWhatsappProfileConnected;
    const hasAutomations = Array.isArray((flows as any)?.data)
        ? (flows as any).data.some((flow: any) => !flow?.deleted)
        : false;
    const phone = wabaAccount?.phones?.[0];
    const activePhoneNumber = phone?.phoneNumber;
    const selectedPhoneNumberForSummary =
        activePhoneNumber ||
        wabaAccount?.phoneNumber ||
        (phone as any)?.phone ||
        selectedByonPhoneNumber?.phoneNumber ||
        selectedCatalogPhoneNumber?.phoneNumber ||
        pendingCatalogPhone?.phoneNumber ||
        "";
    const hasPhone = Boolean(phone?.active && !phone?.deleted);
    const effectiveHasPhone = hasPhone || hasRegisteredOnboardingPhone;

    const isByonPhone = Boolean(selectedByonPhoneNumber?.phoneNumber || (phone as any)?.isByon);
    const phoneIso = selectedByonPhoneNumber?.isoCountry || selectedCatalogPhoneNumber?.isoCountry || pendingCatalogPhone?.isoCountry || (phone as any)?.iso || (phone as any)?.isoCountry || "US";
    const hasDedicatedPlan = Boolean(pricingPlans?.some(p => p.isDedicatedToShopId));
    const filteredPricingPlans: PricingPlanProps[] | null = pricingPlans
        ? hasDedicatedPlan
            ? pricingPlans.filter(p => p.isDedicatedToShopId)
            : (isByonPhone || phoneIso === "US")
                ? pricingPlans.filter(p => [9, 10, 11, 12].includes(p.id!))
                : pricingPlans.filter(p => [14, 15, 16, 17].includes(p.id!))
        : null;

    const hasPublicKey = apiKeys.some((k) => k.key_type === "public" && k.status === "active");
    const hasSecretKey = apiKeys.some((k) => k.key_type === "secret" && k.status === "active");
    const keysDone = hasPublicKey && hasSecretKey;
    const activePublicKeyRow = apiKeys.find((k) => k.key_type === "public" && k.status === "active");
    const activeSecretKeyRow = apiKeys.find((k) => k.key_type === "secret" && k.status === "active");
    const publicKeyValue = revealedKeys.public || activePublicKeyRow?.key || "";
    const secretKeyValue = revealedKeys.secret || activeSecretKeyRow?.key || "";
    const publicKeyForSnippets = publicKeyValue || `${activePublicKeyRow?.key_prefix || "vlv_pub_"}...`;
    const secretKeyForSnippets = secretKeyValue || `${activeSecretKeyRow?.key_prefix || "vlv_sec_"}...`;
    const regenerateKeysForKeysStep = async () => {
        if (!selectedDomain || isAutoRegeneratingKeysRef.current) return;
        isAutoRegeneratingKeysRef.current = true;
        setKeysLoading(true);
        try {
            const activeKeyRows = apiKeys.filter((k) => (k.key_type === "public" || k.key_type === "secret") && k.status === "active");
            for (const keyRow of activeKeyRows) {
                if (!keyRow?.id) continue;
                await revokeV2ApiKey(selectedDomain, Number(keyRow.id));
            }

            const [publicRes, secretRes] = await Promise.all([
                createV2ApiKey(selectedDomain, "public", []),
                createV2ApiKey(selectedDomain, "secret", []),
            ]);

            setRevealedKeys({
                public: publicRes?.data?.key || "",
                secret: secretRes?.data?.key || "",
            });
            await loadApiKeys();
        } finally {
            setKeysLoading(false);
            isAutoRegeneratingKeysRef.current = false;
        }
    };

    const currentStep = stepStack[stepStack.length - 1];
    const nextCtaTitle = currentStep === "pricing" ? tr.cta_select_plan
        : currentStep === "whatsapp" ? tr.cta_select_phone
        : currentStep === "keys" ? tr.cta_get_keys
        : null;
    const fullyOnboarded = Boolean(hasPlan && hasWaba && effectiveHasPhone);
    const isCopilotTestingMode = useMemo(() => {
        if (typeof window === "undefined") return false;
        const host = window.location.hostname;
        const isLocalHost = host === "localhost" || host === "127.0.0.1";
        if (!isLocalHost) return false;
        return window.localStorage.getItem("v2_copilot_testing_mode") === "1";
    }, []);
    const platformIdForFlow = useMemo(() => {
        if (!isCopilotTestingMode) return platformId;
        if (typeof window === "undefined") return platformId;
        const overrideRaw = String(window.localStorage.getItem("v2_copilot_platform_override") || "").toLowerCase().trim();
        if (overrideRaw === "shopify" || overrideRaw === "1") return 1;
        if (overrideRaw === "woo" || overrideRaw === "woocommerce" || overrideRaw === "3") return 3;
        if (overrideRaw === "independent" || overrideRaw === "standalone" || overrideRaw === "2") return 2;
        return platformId;
    }, [isCopilotTestingMode, platformId]);
    const isShopifyOrWoo = platformIdForFlow === 1 || platformIdForFlow === 3;
    const appendAssistantStep = (type: ChatEntry["type"]) => {
        if (!type) return;
        setChatMessages((prev) => [
            ...prev,
            { id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: "assistant", type },
        ]);
    };
    const appendAssistantText = (text: string, type: ChatEntry["type"] = "text") => {
        setChatMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: "assistant", type, text },
        ]);
    };
    const { step2State, setStep2State, goStep2, isStep2TypeActive } = useStep2Flow();

    const { showToast, closeToast } = useToast();
    const { startPricingPlanFlow } = usePaymentFlow();
    const { isFlagOn } = useFlag();
    const isV2Enabled = isFlagOn("v2");

    const resetCopilotStateForDomain = () => {
        setStepStack([]);
        setApiKeys([]);
        setRevealedKeys({});
        setKeysLoading(false);
        setPricingPlans(null);
        setPricingPlansLoading(false);
        setPricingPlansIso("US");
        setChatMessages([{ id: "welcome-1", role: "assistant", type: "welcome" }]);
        setChatInput("");
        setIsThinking(false);
        setConversationId(null);
        setShowPricingPlaceholder(false);
        setNextCyclePricingPlanId(null);
        setPhoneStepCompleted(false);
        setPhoneChoice(null);
        setShowKeysAfterOnboarding(false);
        setSelectedCatalogPhoneNumber(null);
        setSelectedByonPhoneNumber(null);
        setIsSavingPhoneSelection(false);
        setIsSendingByonCode(false);
        setIsVerifyingByonCode(false);
        setByonCode("");
        setByonVerifyError(null);
        setByonVerifyAttempts(0);
        setHasRegisteredOnboardingPhone(false);
        setPendingCatalogPhone(null);
        setIsMetaInProgress(false);
        setStep2State("S2_CHOICE");
        autoRegeneratedForCurrentKeysStepRef.current = false;
    };

    const setStep = (next: StepKey) => {
        setStepStack((prev) => {
            if (prev[prev.length - 1] === next) return prev;
            return [...prev, next];
        });
    };

    const loadApiKeys = async () => {
        if (!selectedDomain) return;
        setKeysLoading(true);
        const res = await listV2ApiKeys(selectedDomain);
        if (res?.data?.keys) setApiKeys(res.data.keys);
        setKeysLoading(false);
    };

    const loadPricingPlans = async () => {
        if(!selectedDomain) return;
        setPricingPlansLoading(true);
        const response = await getV2PricingPlans(selectedDomain, pricingPlansIso);
        setPricingPlans(response.data);
        setPricingPlansLoading(false);
    }

    const loadCycleChanges = async () => {
        if(selectedDomain) {
            const nextCycleResponse = await getNextCyclePricingPlan(selectedDomain);
            if (nextCycleResponse?.data?.nextPricingPlanId) {
                setNextCyclePricingPlanId(nextCycleResponse.data.nextPricingPlanId);
            } else {
                setNextCyclePricingPlanId(null);
            }
        }
    }

    const beforeOnSelect = async () => {
        if(selectedDomain) {
            loadCycleChanges();
        }
    }

    const onSelectPricingPlan = () => {
        loadShop();
    }

    const stopShopifyPoll = () => {
        if (shopifyPollIntervalRef.current) {
            clearInterval(shopifyPollIntervalRef.current);
            shopifyPollIntervalRef.current = null;
        }
        shopifyPollAttemptsRef.current = 0;
    };

    const onShopifyBillingRedirect = () => {
        stopShopifyPoll();
        shopifyPollAttemptsRef.current = 0;
        shopifyPollIntervalRef.current = setInterval(async () => {
            shopifyPollAttemptsRef.current += 1;
            if (shopifyPollAttemptsRef.current > 24) {
                stopShopifyPoll();
                return;
            }
            await loadShop();
        }, 5000);
    };

    const afterSuccessPlanSwitch = (successDetails: any) => {
        const same = successDetails?.same;
        const downgrade = successDetails?.downgrade;
        const invoiceNumber = successDetails?.invoiceNumber;
        if (onSelectPricingPlan) {
            const text = downgrade ?
                'Your pricing plan was updated. You’ll continue enjoying your previous plan’s benefits until the end of your billing cycle.' :
                same ? 'You’re all set! Your current plan will stay active with no changes at the end of this cycle.' :
                    `Payment successful! Your pricing plan is now active.${invoiceNumber ? ` Invoice No. ${invoiceNumber} has been issued.` : ''}`;
            showToast({ skin: ToastSkin.approval, displayTime: 15000, text, buttons: [
                    { text: 'Ok', onClick: closeToast },
                ]});
            beforeOnSelect();
            onSelectPricingPlan();
        } else {
            window.location.reload();
        }
    }

    const onChangingPricingPlan = async (pricingPlanId: number) => {
        if (!selectedDomain) return;
        const targetPlan = pricingPlans?.find((pp) => pp.id === pricingPlanId);
        const targetFee = targetPlan?.fee || 0;
        const currentFee = currentPricingPlan?.fee || 0;
        const amountUsd = targetFee - currentFee;
        const description = targetFee > currentFee ? (
            <>
                <div className="reduct-price">This cycle: Only the difference - ${Math.round(amountUsd * 1000) / 1000}</div>
                <div className="monthly-price">Next cycle: Monthly price - ${Math.round(targetFee * 1000) / 1000}</div>
            </>
        ) : targetFee < currentFee ? (
            <>
                <div className="note">Your previous plan stays active until the next cycle.</div>
                <div className="price">${targetFee} <span>Starting next billing cycle</span></div>
            </>
        ) : null;

        await startPricingPlanFlow({
            selectedDomain,
            platformId: Number(shop.data?.platformId || 0),
            pricingPlanId,
            title: `${targetPlan?.title || ""} Pricing Plan`,
            amountUsd,
            description,
            onSuccess: afterSuccessPlanSwitch,
            onDirectSuccess: async () => {
                await beforeOnSelect();
                onSelectPricingPlan();
            },
            onShopifyRedirect: onShopifyBillingRedirect,
        });
    };

    const onPhoneDecision = async ({ isByon, phone }: { isByon: boolean, phone: CreatePhoneRequest }) => {
        if(!selectedDomain || !waba?.data?.waba?.id) return;
        const response = await createPhoneNumber(selectedDomain, waba.data.waba.id, phone, isByon);
        if(response.data === 'ok') {
            loadWaba();
            return response.data;
        } else {
            return null;
        }
    }

    useEffect(() => {
        resetCopilotStateForDomain();
        loadShop();
        loadWaba();
    }, [selectedDomain]);

    useEffect(() => {
        if (hasPlan && shopifyPollIntervalRef.current) {
            stopShopifyPoll();
        }
    }, [hasPlan]);

    useEffect(() => {
        return () => stopShopifyPoll();
    }, []);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalHeight = document.body.style.height;
        document.body.style.overflow = "hidden";
        document.body.style.height = "100%";
        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.height = originalHeight;
        };
    }, []);

    useEffect(() => {
        loadApiKeys();
    }, [selectedDomain]);

    useEffect(() => {
        const loadPhoneStatus = async () => {
            if (!selectedDomain) return;
            const response = await GetV2OnboardingPhoneStatus(selectedDomain);
            if (response.ok) {
                setHasRegisteredOnboardingPhone(Boolean(response.data?.has_phone));
                if (response.data?.pending_phone?.phoneNumber && response.data?.pending_phone?.isoCountry) {
                    setPendingCatalogPhone({ phoneNumber: response.data.pending_phone.phoneNumber, isoCountry: response.data.pending_phone.isoCountry });
                }
            }
        };
        loadPhoneStatus();
    }, [selectedDomain]);

    useEffect(() => {
        const loadWhatsappProfileStatus = async () => {
            if (!selectedDomain) {
                setIsWhatsappProfileConnected(false);
                setIsWhatsappProfileStatusLoaded(true);
                return;
            }
            if (!wabaAccount?.providerSenderId) {
                setIsWhatsappProfileConnected(false);
                setIsWhatsappProfileStatusLoaded(true);
                return;
            }
            setIsWhatsappProfileStatusLoaded(false);
            try {
                const response = await getWhatsappProfile(selectedDomain);
                const hasProfile = Boolean(response?.data?.profile);
                setIsWhatsappProfileConnected(hasProfile);
            } catch (error) {
                setIsWhatsappProfileConnected(false);
            } finally {
                setIsWhatsappProfileStatusLoaded(true);
            }
        };
        loadWhatsappProfileStatus();
    }, [selectedDomain, wabaAccount?.providerSenderId, wabaAccount?.id]);

    useEffect(() => {
        loadPricingPlans();
    }, [selectedDomain, pricingPlansIso]);

    useEffect(() => {
        if (!isShopLoaded || !isWhatsappProfileStatusLoaded) return;
        if (!effectiveHasPhone) {
            setStepStack(["whatsapp"]);
            return;
        }
        if (!hasPlan) {
            setStepStack(["pricing"]);
            return;
        }
        if (!hasWaba) {
            setStepStack(["whatsapp"]);
            return;
        }
        if (showKeysAfterOnboarding && !isShopifyOrWoo) {
            setStepStack(["keys"]);
            return;
        }
        setStepStack(["done"]);
    }, [isShopLoaded, isWhatsappProfileStatusLoaded, hasPlan, hasWaba, effectiveHasPhone, showKeysAfterOnboarding, isShopifyOrWoo]);

    const ensureStep = (type: ChatEntry["type"]) => {
        if (!type) return;
        setChatMessages((prev) => {
            if (prev.some((m) => m.type === type)) return prev;
            return [...prev, { id: `${type}-${Date.now()}`, role: "assistant", type }];
        });
    };
    const replayCurrentStep = () => {
        if (currentStep === "pricing") {
            setShowPricingPlaceholder(true);
            appendAssistantStep("pricing");
            return;
        }
        if (currentStep === "whatsapp") {
            // If a phone already exists, retry Meta connection flow (Step 3), not Step 2.
            if (effectiveHasPhone) {
                setStep2State("S2_DONE");
                appendAssistantStep("whatsapp_meta");
                return;
            }
            setPhoneStepCompleted(false);
            setStep2State("S2_CHOICE");
            appendAssistantStep("whatsapp");
            appendAssistantStep("whatsapp_choice");
            return;
        }
        if (currentStep === "keys") {
            appendAssistantStep("keys");
        }
    };

    // const updateByonPhone = async () => {
    //     if(!selectedCountryCode || !selectedPhoneNumber || !selectedPhoneNumberObj.isoCountry) return;
    //     const phone = { phone: cleanPhoneNumber(selectedCountryCode.toString(), selectedPhoneNumber), price: 0, isoCountry: selectedPhoneNumberObj.isoCountry };
    //     if(isValidPhoneNumber(phone.phone)) {
    //         setIsLoading(true);
    //         const response = await onSubmit({ phone, isByon: isByonRef.current });
    //         // console.log("update BYON phone number response: ", response);
    //         setIsLoading(false);
    //         goNext();
    //         loadWaba();
    //         return true;
    //     } else {
    //         showToast({ text: 'Phone number is not valid, please try again', skin: ToastSkin.error});
    //         return false;
    //     }
    // }

    const updateCatalogPhone = async () => {
        if(!selectedCatalogPhoneNumber) return;
        if (!selectedDomain || !selectedCatalogPhoneNumber.phoneNumber || selectedCatalogPhoneNumber.price === undefined || !selectedCatalogPhoneNumber.isoCountry || !selectedCatalogPhoneNumber.token) return;
        setIsSavingPhoneSelection(true);
        const mockModeRaw = isCopilotTestingMode
            ? String(window.localStorage.getItem("v2_mock_twilio_purchase") || "").toLowerCase()
            : "";
        const mockMode = (mockModeRaw === "success" || mockModeRaw === "failure")
            ? mockModeRaw as "success" | "failure"
            : undefined;
        const response = await RegisterV2OnboardingPhoneNumber(selectedDomain, {
            phone: selectedCatalogPhoneNumber.phoneNumber,
            price: selectedCatalogPhoneNumber.price,
            iso_country: selectedCatalogPhoneNumber.isoCountry,
            is_byon: false,
            token: selectedCatalogPhoneNumber.token,
            mock_twilio_purchase: mockMode,
        });
        setIsSavingPhoneSelection(false);
        if (!response.ok) {
            appendAssistantText(response.error || "Failed to register this number. Please try another number.");
            return false;
        }
        setHasRegisteredOnboardingPhone(true);
        loadWaba();
        if (!hasPlan) {
            setStep("pricing");
            ensureStep("pricing");
            setShowPricingPlaceholder(true);
        }
        return true;
    }

    const sendByonCode = async (): Promise<boolean> => {
        if (!selectedDomain || !selectedByonPhoneNumber?.phoneNumber) return false;
        setIsSendingByonCode(true);
        const response = await SendV2ByonPhoneCode(selectedDomain, {
            phoneNumber: selectedByonPhoneNumber.phoneNumber,
            isoCountry: selectedByonPhoneNumber.isoCountry,
        });
        setIsSendingByonCode(false);

        if (!response.ok) {
            appendAssistantText(response.error || "Failed to send verification code. Please check the phone number and try again.");
            return false;
        }
        setByonVerifyError(null);
        setByonVerifyAttempts(0);
        return true;
    };

    const verifyByonCode = async (): Promise<"success" | "failed" | "restart"> => {
        if (!selectedDomain || !selectedByonPhoneNumber?.phoneNumber || !byonCode.trim()) return "failed";
        setIsVerifyingByonCode(true);
        const response = await VerifyV2ByonPhoneCode(selectedDomain, byonCode.trim(), {
            phoneNumber: selectedByonPhoneNumber.phoneNumber,
            isoCountry: selectedByonPhoneNumber.isoCountry,
        });
        setIsVerifyingByonCode(false);

        if (!response.ok || !response.data?.verified) {
            const attempts = byonVerifyAttempts + 1;
            if (attempts >= 3) {
                setByonVerifyAttempts(0);
                setByonVerifyError(null);
                setByonCode("");
                return "restart";
            }
            setByonVerifyAttempts(attempts);
            const normalizedError = String(response.error || "").toLowerCase();
            const friendlyError = normalizedError.includes("invalid_code")
                ? "The verification code is incorrect. Please check the SMS and try again."
                : (response.error || "Verification failed. Please try again.");
            setByonVerifyError(friendlyError);
            return "failed";
        }

        setByonCode("");
        setByonVerifyAttempts(0);
        setByonVerifyError(null);
        setHasRegisteredOnboardingPhone(true);
        loadWaba();
        return "success";
    };

    useEffect(() => {
        if (!isShopLoaded || !isWhatsappProfileStatusLoaded) return;
        if (currentStep === "whatsapp") {
            ensureStep("whatsapp");
            if ((effectiveHasPhone || phoneStepCompleted) && hasPlan) {
                if (step2State !== "S2_DONE") {
                    goStep2("S2_DONE", appendAssistantStep);
                }
            } else if (!effectiveHasPhone && !phoneStepCompleted) {
                if (step2State === "S2_DONE") {
                    setStep2State("S2_CHOICE");
                }
                const hasAnyStep2Bubble = chatMessages.some((m) => isStep2MessageType(m.type));
                if (!hasAnyStep2Bubble) {
                    goStep2("S2_CHOICE", appendAssistantStep);
                }
            }
        }
        if (currentStep === "pricing") {
            ensureStep("pricing");
            setShowPricingPlaceholder(true);
        }
        if (currentStep === "keys") ensureStep("keys");
        if (currentStep === "done") ensureStep("done");
    }, [isShopLoaded, isWhatsappProfileStatusLoaded, currentStep, effectiveHasPhone, phoneStepCompleted, step2State, chatMessages]);

    useEffect(() => {
        if (currentStep !== "keys") {
            autoRegeneratedForCurrentKeysStepRef.current = false;
            return;
        }
        if (autoRegeneratedForCurrentKeysStepRef.current) return;
        autoRegeneratedForCurrentKeysStepRef.current = true;
        regenerateKeysForKeysStep();
    }, [currentStep, selectedDomain]);


    const accountContext = useMemo(() => {
        const defaultLang = uiLang === "he" ? "Hebrew" : "English";
        const langNote = `Respond in the same language the user writes in; default to ${defaultLang} if they haven't written yet.`;
        const platformLabel = platformId === 1 ? "shopify" : platformId === 3 ? "woocommerce" : platformId === 2 ? "standalone" : "unknown";
        const shopConnected = isShopifyOrWoo; // shop record exists for Shopify/WooCommerce once app is installed
        const phonePurchased = hasPhone; // active=true means Twilio purchase completed
        const phoneSelectedOnly = effectiveHasPhone && !hasPhone; // picked in UI but not yet purchased

        const nextStep = !effectiveHasPhone
            ? "phone_number_selection — user must pick a catalog number or bring their own number"
            : !hasPlan
                ? "pricing_plan_selection — user must choose a pricing plan; phone purchase happens automatically after this"
                : !hasWaba
                    ? "whatsapp_meta_connection — user must complete Meta embedded onboarding to link the phone number to WhatsApp Business"
                    : isShopifyOrWoo
                        ? "automation_creation — onboarding complete, ready to build automations"
                        : !keysDone
                            ? "api_keys_and_sdk — user must generate API keys and install the SDK snippet on their website"
                            : "automation_creation — onboarding complete, ready to build automations";

        const parts: string[] = [];
        parts.push("=== SHOP ONBOARDING STATUS (use this — do NOT ask the user what state they are in) ===");

        // Phase 1 — always complete for logged-in shops
        parts.push("phase_1: completed (user is logged in)");
        parts.push(`contact_details: submitted — email: ${shop?.data?.contactEmail || "on file"}, phone: ${shop?.data?.contactPhone || "on file"}`);
        parts.push("email_verified: yes");
        parts.push("phone_verified: yes");
        parts.push(`platform_selected: yes — platform: ${platformLabel}`);
        parts.push(`shop_connected: ${shopConnected ? "yes" : "no"} — ${isShopifyOrWoo ? platformLabel + " app/plugin installed and shop record exists" : "standalone site, no app install required"}`);

        // Phase 2
        parts.push("--- phase_2 ---");
        parts.push(`phone_number_selected: ${effectiveHasPhone ? "yes" : "no"}${effectiveHasPhone && activePhoneNumber ? ` — number: ${activePhoneNumber}` : ""}`);
        parts.push(`phone_number_purchased_locked: ${phonePurchased ? "yes" : phoneSelectedOnly ? "no (selected in UI, pending purchase after plan confirmation)" : "no"}`);
        parts.push(`pricing_plan: ${hasPlan ? `yes — plan: ${currentPricingPlan?.title || "active plan"}` : "not selected yet"}`);
        parts.push(`whatsapp_meta_connected: ${hasWaba ? "yes" : "no"}`);
        if (!isShopifyOrWoo) {
            parts.push(`api_keys_configured: ${keysDone ? "yes" : "no"}`);
        }
        parts.push(`fully_onboarded: ${fullyOnboarded ? "yes" : "no"}`);
        parts.push(`next_step: ${nextStep}`);
        parts.push(`platform_branch: ${isShopifyOrWoo ? "shopify_or_woocommerce (no SDK/keys needed)" : "standalone (SDK, keys, identify, and event tracking required after WhatsApp connection)"}`);
        parts.push(langNote);

        return parts.join("\n");
    }, [hasPlan, hasWaba, effectiveHasPhone, hasPhone, keysDone, isShopifyOrWoo, platformId, currentPricingPlan, activePhoneNumber, fullyOnboarded, shop?.data?.contactEmail, shop?.data?.contactPhone, uiLang]);

    const scrollToBottom = () => {
        const spacer = document.getElementById("messages-spacer");
        spacer?.scrollIntoView({ behavior: "smooth" });
    };

    useLayoutEffect(() => {
        if (autoScrollRef.current) scrollToBottom();
    }, [chatMessages.length, isThinking]);

    useEffect(() => {
        if (!autoScrollRef.current) return;
        const t1 = window.setTimeout(scrollToBottom, 0);
        const t2 = window.setTimeout(scrollToBottom, 50);
        const t3 = window.setTimeout(scrollToBottom, 150);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
        };
    }, [chatMessages.length, isThinking]);

    const keysView = (
        <div className="v2oa-step">
            <div className="v2oa-step-title"><span>1</span>Keys & Snippets</div>
            <div className="v2oa-guide">
                Generate keys for your website and add the SDK/Identify snippets.
            </div>
            <div className="v2oa-actions">
                {!hasPublicKey ? (
                    <Button
                        type="primary"
                        onClick={async () => {
                            if (!selectedDomain) return;
                            const response = await createV2ApiKey(selectedDomain, "public", []);
                            const rawKey = response?.data?.key;
                            if (rawKey) {
                                setRevealedKeys((prev) => ({ ...prev, public: rawKey }));
                            }
                            await loadApiKeys();
                        }}
                        loading={keysLoading}
                    >
                        Generate public key
                    </Button>
                ) : null}
                {!hasSecretKey ? (
                    <Button
                        type="primary"
                        onClick={async () => {
                            if (!selectedDomain) return;
                            const response = await createV2ApiKey(selectedDomain, "secret", []);
                            const rawKey = response?.data?.key;
                            if (rawKey) {
                                setRevealedKeys((prev) => ({ ...prev, secret: rawKey }));
                            }
                            await loadApiKeys();
                        }}
                        loading={keysLoading}
                    >
                        Generate secret key
                    </Button>
                ) : null}
            </div>

            {hasPublicKey || hasSecretKey ? (
                <div className="v2oa-step-3-row">
                    <div className="v2oa-ki-row">
                        {hasPublicKey ? (
                            <div className="v2oa-ki-col">
                                <div className="v2oa-code-block">
                                    <label>Public key (browser SDK)</label>
                                    <CopyToClipboardCode content={publicKeyValue || (activePublicKeyRow?.key_prefix ? `${activePublicKeyRow.key_prefix}...` : "Hidden")} />
                                </div>
                            </div>
                        ) : null}
                        {hasSecretKey ? (
                            <div className="v2oa-ki-col">
                                <div className="v2oa-code-block">
                                    <label>Secret key (server identify)</label>
                                    <CopyToClipboardCode content={secretKeyValue || (activeSecretKeyRow?.key_prefix ? `${activeSecretKeyRow.key_prefix}...` : "Hidden")} />
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <Tabs
                        size="small"
                        items={[
                            {
                                key: "npm",
                                label: "NPM (recommended)",
                                children: (
                                    <div>
                                        <label>Install</label>
                                        <CopyToClipboardCode content={`npm i @vuelve/sdk`} />
                                        <label>Initialize</label>
                                        <CopyToClipboardCode content={`import { loadVuelve, identify, track } from "@vuelve/sdk";\n\nloadVuelve({\n  publicKey: "${publicKeyForSnippets}",\n  baseUrl: "http://vuelve-server.fly.dev"\n});`} />
                                        <label>Use</label>
                                        <CopyToClipboardCode content={`identify({ client_id: "user_123" });`} />
                                        <CopyToClipboardCode content={`track("room.view", { context: { room_name: "Suite" } });`} />
                                        <label>Server identify (with secret key)</label>
                                        <CopyToClipboardCode content={`POST /v2/identify\nAuthorization: Bearer ${secretKeyForSnippets}\nContent-Type: application/json\n\n{\n  "client_id": "user_123",\n  "email": "a@b.com",\n  "phone": "+14155551234"\n}`} />
                                        <label>Server track (trusted backend events)</label>
                                        <CopyToClipboardCode content={`POST /v2/track\nAuthorization: Bearer ${secretKeyForSnippets}\nContent-Type: application/json\n\n{\n  "client_id": "user_123",\n  "event_name": "order.created",\n  "entity_type": "order",\n  "entity_id": "98765",\n  "meta": {\n    "context": {\n      "order_id": "98765",\n      "order_total": 149.9,\n      "currency": "USD"\n    }\n  }\n}`} />
                                    </div>
                                ),
                            },
                            {
                                key: "script",
                                label: "Script tag",
                                children: (
                                    <div>
                                        <label>Install</label>
                                        <CopyToClipboardCode content={SDK_SCRIPT(publicKeyForSnippets)} />
                                        <label>TypeScript types (optional)</label>
                                        <CopyToClipboardCode maxLength={1000} content={TYPES_DTS} />
                                        <label>Use</label>
                                        <CopyToClipboardCode content={`window.vuelve?.identify({ client_id: "user_123" });`} />
                                        <CopyToClipboardCode content={`window.vuelve?.track("room.view", { context: { room_name: "Suite" } });`} />
                                        <label>Server identify (with secret key)</label>
                                        <CopyToClipboardCode content={`POST /v2/identify\nAuthorization: Bearer ${secretKeyForSnippets}\nContent-Type: application/json\n\n{\n  "client_id": "user_123",\n  "email": "a@b.com",\n  "phone": "+14155551234"\n}`} />
                                        <label>Server track (trusted backend events)</label>
                                        <CopyToClipboardCode content={`POST /v2/track\nAuthorization: Bearer ${secretKeyForSnippets}\nContent-Type: application/json\n\n{\n  "client_id": "user_123",\n  "event_name": "order.created",\n  "entity_type": "order",\n  "entity_id": "98765",\n  "meta": {\n    "context": {\n      "order_id": "98765",\n      "order_total": 149.9,\n      "currency": "USD"\n    }\n  }\n}`} />
                                    </div>
                                ),
                            },
                        ]}
                    />
                </div>
            ) : null}
        </div>
    );

    const whatsappIntroView = (
        <StepIntroBubble
            className="v2oa-step"
            stepNumber={1}
            title={tr.whatsapp_intro_title}
            paragraphs={[
                tr.whatsapp_intro_p1,
                tr.whatsapp_intro_p2,
                tr.whatsapp_intro_p3,
                tr.whatsapp_intro_p4,
            ]}
        />
    );

    const sendChat = async () => {
        if (!chatInput.trim() || isThinking) return;
        const text = chatInput.trim();
        setChatInput("");
        const normalized = text.toLowerCase();
        const wantsPricing = normalized.includes("select a pricing plan")
            || normalized.includes(tr.cta_select_plan.toLowerCase())
            || (nextCtaTitle && normalized.includes(nextCtaTitle.toLowerCase()));
        if (showPricingPlaceholder && !hasPlan) {
            setShowPricingPlaceholder(false);
            setChatMessages((prev) => prev.filter((m) => m.type !== "pricing"));
        }
        if (wantsPricing && !hasPlan) {
            setShowPricingPlaceholder(true);
            ensureStep("pricing");
        }
        setChatMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: "user", type: "text", text }]);
        setIsThinking(true);
        const res = await postAgentChat(selectedDomain || "", {
            conversation_id: conversationId,
            message: text,
            input: {
                context_type: "account_onboarding",
                onboarding_context: accountContext,
            },
        });
        setIsThinking(false);
        if (!res.ok) {
            setChatMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", type: "text", text: tr.chat_error }]);
            return;
        }
        setConversationId(res.data!.conversation_id);
        const rtl = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(res.data!.reply || "");
        setChatMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", type: "text", text: res.data!.reply || "", isRtl: rtl }]);
    };

    const renderMessage = (m: ChatEntry, canInteract: boolean) => {
        if (m.type === "welcome") {
            if (!isShopLoaded) return null;
            if (hasPlan && effectiveHasPhone && hasWaba) return null;
            const plan = currentPricingPlan || selectedPricingPlan;
            const planName = plan?.title || "your selected plan";
            const includesPhone = Boolean(plan?.isPhoneNumberIncluded);
            const includedIso = plan?.includedPhoneNumberIso;
            const phoneIncludedText = includesPhone
                ? includedIso ? tr.phone_included_iso(includedIso) : tr.phone_included_generic
                : "";
            return (
                <div className="v2oa-text">
                    {!hasPlan && !effectiveHasPhone ? (
                        <>
                            <div className="v2oa-title">{tr.welcome_title}</div>
                            <div>{tr.welcome_intro}</div>
                            <ul>
                                <li>{tr.step_phone}</li>
                                <li>{tr.step_pricing}</li>
                                <li>{tr.step_waba}</li>
                                {!isShopifyOrWoo ? <li>{tr.step_keys}</li> : null}
                            </ul>
                            <div>{tr.welcome_after}</div>
                        </>
                    ) : !hasPlan && effectiveHasPhone ? (
                        <>
                            <div className="v2oa-title">{tr.phone_selected_title}</div>
                            <div>{isByonPhone ? tr.phone_selected_body_byon(selectedPhoneNumberForSummary || "your chosen number") : tr.phone_selected_body_twilio(selectedPhoneNumberForSummary || "your chosen number")}</div>
                            {!isByonPhone && <div>{tr.phone_selected_next_twilio}</div>}
                        </>
                    ) : hasPlan && !effectiveHasPhone ? (
                        <>
                            <div className="v2oa-title">{tr.almost_title}</div>
                            <div>{tr.almost_plan(planName)}{phoneIncludedText}</div>
                            <div>
                                {tr.change_plan_prefix}{" "}
                                <a href="/settings?section=payments">{tr.settings_payments}</a>.
                            </div>
                            <div>{tr.almost_next}</div>
                        </>
                    ) : hasPlan && effectiveHasPhone && !hasWaba ? (
                        <>
                            <div className="v2oa-title">{tr.connect_waba_title}</div>
                            <div>
                                {tr.your_plan_is} <b>{planName}</b>. {tr.to_change_visit}{" "}
                                <a href="/settings?section=payments">{tr.settings_payments}</a>.
                            </div>
                            <div>{tr.your_phone_is} <b>{activePhoneNumber || "ready"}</b>.</div>
                            <div>{tr.connect_waba_next}</div>
                        </>
                    ) : null}
                </div>
            );
        }
        if (m.type === "pricing") return null;
        if (m.type === "whatsapp_summary") {
            return null;
        }
        if (m.type === "whatsapp") {
            // Once phone is already registered, skip the Step 2 intro bubble and keep only Meta step.
            if (effectiveHasPhone) return null;
            return whatsappIntroView;
        }
        if (m.type === "whatsapp_meta") {
            return (
                <Step3MetaFlowBubble
                    lang={uiLang}
                    canInteract={canInteract}
                    isActive={isStep2TypeActive("whatsapp_meta")}
                    showTestingActions={isCopilotTestingMode}
                    activePhoneNumber={activePhoneNumber}
                    selectedByonPhoneNumber={selectedByonPhoneNumber}
                    selectedCatalogPhoneNumber={selectedCatalogPhoneNumber}
                    onMetaStarted={() => {
                        setIsMetaInProgress(true);
                        appendAssistantText(tr.meta_opened, "meta_progress");
                    }}
                    onMetaCompleted={() => {
                        setIsMetaInProgress(false);
                        setShowKeysAfterOnboarding(false);
                        appendAssistantText(tr.meta_complete);
                        setStep("done");
                    }}
                    onMetaFailed={() => {
                        setIsMetaInProgress(false);
                        appendAssistantText(tr.meta_failed);
                        appendAssistantStep("whatsapp_meta");
                    }}
                />
            );
        }
        if (isStep2MessageType(m.type)) {
            return (
                <Step2PhoneFlowBubble
                    lang={uiLang}
                    type={m.type}
                    canInteract={canInteract}
                    hasRegisteredPhone={effectiveHasPhone}
                    hasPlan={hasPlan}
                    currentPricingPlan={hasPlan ? currentPricingPlan : null}
                    selectedCatalogPhoneNumber={selectedCatalogPhoneNumber}
                    setSelectedCatalogPhoneNumber={setSelectedCatalogPhoneNumber}
                    selectedByonPhoneNumber={selectedByonPhoneNumber}
                    setSelectedByonPhoneNumber={setSelectedByonPhoneNumber}
                    isSavingPhoneSelection={isSavingPhoneSelection}
                    isSendingByonCode={isSendingByonCode}
                    isVerifyingByonCode={isVerifyingByonCode}
                    byonCode={byonCode}
                    setByonCode={setByonCode}
                    byonVerifyError={byonVerifyError}
                    byonVerifyAttempts={byonVerifyAttempts}
                    isStep2TypeActive={isStep2TypeActive}
                    setPhoneChoice={setPhoneChoice}
                    goStep2={(next) => goStep2(next, appendAssistantStep)}
                    setPhoneStepCompleted={setPhoneStepCompleted}
                    updateCatalogPhone={updateCatalogPhone}
                    sendByonCode={sendByonCode}
                    verifyByonCode={verifyByonCode}
                />
            );
        }
        if (m.type === "keys") return keysView;
        if (m.type === "done") {
            return (
                <div className="v2oa-step">
                    <div className="v2oa-step-title">{tr.done_step_title}</div>
                    <div className="v2oa-guide">{tr.done_phone_connected}</div>
                    <div className="v2oa-text">
                        <div><b>{tr.done_selected_plan}</b> {currentPricingPlan?.title || selectedPricingPlan?.title || tr.done_not_available}</div>
                        <div><b>{tr.done_selected_phone}</b> {selectedPhoneNumberForSummary || tr.done_not_available}</div>
                        <div>
                            <b>{tr.done_waba_label}</b>{" "}
                            {hasWaba ? tr.done_connected : tr.done_not_connected}
                        </div>
                    </div>
                    {isShopifyOrWoo ? (
                        <div className="v2oa-text">
                            <div><b>{tr.done_shopwoo_title}</b></div>
                            <div>{tr.done_shopwoo_body}</div>
                            <div className="v2oa-actions">
                                <Button type="primary" onClick={() => navigate("/automations")}>
                                    {tr.done_start_automations}
                                </Button>
                            </div>
                        </div>
                    ) : !hasAutomations ? (
                        <div className="v2oa-text">
                            <div><b>{tr.done_full_title}</b></div>
                            <div>{tr.done_full_body}</div>
                            <div>{tr.done_tracking}</div>
                            <div>{tr.done_tracking_next}</div>
                            <div className="v2oa-inline-actions">
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        setShowKeysAfterOnboarding(true);
                                        setStep("keys");
                                    }}
                                >
                                    {tr.done_show_snippets}
                                </Button>
                                <Button type="default" disabled>
                                    {tr.done_start_automations}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="v2oa-text">
                            <div><b>{tr.done_full_title}</b></div>
                            <div>{tr.done_full_body}</div>
                            <div>{tr.done_continue}</div>
                        </div>
                    )}
                </div>
            );
        }
        if (m.type === "meta_progress") {
            return (
                <div className="v2oa-text">
                    <div>{m.text}</div>
                    {isMetaInProgress ? (
                        <div className="v2oa-loader">
                            <span />
                            <span />
                            <span />
                        </div>
                    ) : null}
                </div>
            );
        }
        if (!m.text || !String(m.text).trim()) return null;
        return (
            <div className={`v2oa-text ${m.isRtl ? "v2oa-rtl" : ""}`} dir={m.isRtl ? "rtl" : "ltr"}>
                {m.text}
            </div>
        );
    };


    return (
        <>
            <div className="v2oa-page modal" dir={isRtlUI ? "rtl" : "ltr"}>
                <div className="bg" />
                <div className="v2oa-lang-corner">
                    <details className="v2oa-lang-picker">
                        <summary className="v2oa-lang-trigger">
                            <img className="v2oa-lang-flag" src={currentLangOption.flagSrc} alt="" aria-hidden="true" />
                            <span className="v2oa-lang-label">{currentLangOption.label}</span>
                        </summary>
                        <div className="v2oa-lang-menu" role="listbox" aria-label="Select language">
                            {LANG_OPTIONS.map((opt) => (
                                <button
                                    key={opt.code}
                                    className={`v2oa-lang-option${uiLang === opt.code ? " v2oa-lang-option-active" : ""}`}
                                    onClick={() => {
                                        setUiLang(opt.code);
                                        localStorage.setItem(VIVO_LANG_KEY, opt.code);
                                        document.querySelectorAll(".v2oa-lang-picker").forEach((el) => (el as HTMLDetailsElement).removeAttribute("open"));
                                    }}
                                    aria-current={uiLang === opt.code ? "true" : undefined}
                                >
                                    <img className="v2oa-lang-flag" src={opt.flagSrc} alt="" aria-hidden="true" />
                                    <span className="v2oa-lang-label">{opt.label}</span>
                                </button>
                            ))}
                        </div>
                    </details>
                </div>
                <div className={`v2oa-body ${chatMessages.length ? "v2oa-body-active" : "v2oa-body-idle"}`}>
                    {!isShopLoaded || !isWhatsappProfileStatusLoaded ? (
                        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <div className="v2oa-loader" style={{ transform: "scale(1.8)" }}>
                                <span />
                                <span />
                                <span />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="v2oa-messages"
                                ref={messagesRef}
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    const threshold = 24;
                                    autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
                                }}
                            >
                                {chatMessages.map((m, idx) => {
                                    const isLast = idx === chatMessages.length - 1;
                                    const allowActions = isLast
                                        && m.role === "assistant"
                                        && (m.type === "text" || (m.type === "welcome" && chatMessages.length === 1));
                                    if (m.type === "pricing") {
                                        return (
                                            <Step1PricingFlowBubble
                                                lang={uiLang}
                                                stepNumber={effectiveHasPhone ? 2 : 1}
                                                key={m.id}
                                                isVisible={showPricingPlaceholder}
                                                isLast={isLast}
                                                pricingPlans={filteredPricingPlans}
                                                hasPlan={hasPlan}
                                                pricingPlanId={pricingPlanId}
                                                onSelectPlan={onChangingPricingPlan}
                                                onChangePhone={() => {
                                                    setSelectedCatalogPhoneNumber(null);
                                                    setSelectedByonPhoneNumber(null);
                                                    setHasRegisteredOnboardingPhone(false);
                                                    setPhoneStepCompleted(false);
                                                    setShowPricingPlaceholder(false);
                                                    setStep2State("S2_CHOICE");
                                                    setStepStack(["whatsapp"]);
                                                    setChatMessages((prev) => {
                                                        const filtered = prev.filter((msg) => msg.type !== "pricing" && msg.type !== "whatsapp" && !isStep2MessageType(msg.type));
                                                        const ts = Date.now();
                                                        return [
                                                            ...filtered,
                                                            { id: `whatsapp-${ts}`, role: "assistant" as const, type: "whatsapp" as const },
                                                            { id: `whatsapp_choice-${ts}`, role: "assistant" as const, type: "whatsapp_choice" as const },
                                                        ];
                                                    });
                                                }}
                                            />
                                        );
                                    }
                                    const content = renderMessage(m, isLast);
                                    if (!content) return null;
                                    return (
                                        <div key={m.id} className={`v2oa-step v2oa-${m.role}`}>
                                            <div className="v2oa-bubble" dir={isRtlUI ? "rtl" : "ltr"}>
                                                {content}
                                                {allowActions ? (
                                                    <div className="v2oa-inline-actions">
                                                        {currentStep === "pricing" ? (
                                                            <Button
                                                                type="primary"
                                                                onClick={() => {
                                                                    setShowPricingPlaceholder(true);
                                                                    ensureStep("pricing");
                                                                }}
                                                            >
                                                                {tr.cta_select_plan}
                                                            </Button>
                                                    ) : currentStep === "whatsapp" ? (
                                                        <Button type="primary" onClick={replayCurrentStep}>
                                                            {effectiveHasPhone ? tr.cta_reconnect_whatsapp : tr.cta_select_phone}
                                                        </Button>
                                                    ) : currentStep === "keys" ? (
                                                            <Button type="primary" onClick={replayCurrentStep}>
                                                                {tr.cta_get_keys}
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                                {isThinking ? (
                                    <div className="v2oa-step v2oa-assistant">
                                        <div className="v2oa-bubble">
                                            <div className="v2oa-loader">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <div id="messages-spacer" className="v2oa-scroll-spacer" />
                            </div>

                            <div className={`v2oa-input-stack ${chatMessages.length ? "v2oa-input-stack-bottom" : ""}`}>
                                <div className={`v2oa-input ${chatMessages.length ? "v2oa-input-bottom" : "v2oa-input-centered"}`}>
                                    <Input.TextArea
                                        rows={1}
                                        autoSize={{ minRows: 1, maxRows: 6 }}
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        placeholder={nextCtaTitle ? tr.placeholder_cta(nextCtaTitle) : tr.placeholder_default}
                                        onPressEnter={(e) => {
                                            if (e.shiftKey) return;
                                            e.preventDefault();
                                            sendChat();
                                        }}
                                    />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<SendFilledIcon />}
                                        onClick={sendChat}
                                        aria-label="Send"
                                        className="v2oa-send-button"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default V2CopilotPage;
